import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tabs, Tab } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from '../Person.module.scss';
import { memo, useEffect, useState } from 'react';
import { cast, handleApiError } from '~/utils/helper';
import { IPerson, IPersonDetail } from '~/utils/interface/person';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppSelector } from '~/redux/hooks';
import personApi from '~/apis/person';
import { useNavigate, useParams } from 'react-router-dom';
import actionLogApi from '~/apis/action-log';
import { IActionLog } from '~/utils/interface/actionLog';
import locationApi from '~/apis/location';
import { ILocation } from '~/utils/interface/location';
import dayjs from 'dayjs';
import { ArrowRight2Icon, ArrowRight3Icon } from '~/assets/icons';
import PersonMessages from './PersonMessages';

const cx = classNames.bind(styles);

const PersonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('admin/person');

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);

  const username = selectedUser?.username ?? userProfile?.username;

  const [person, setPerson] = useState<IPerson>();
  const [personDetail, setPersonDetail] = useState<IPersonDetail>();
  const [actionLogs, setActionLogs] = useState<Array<IActionLog>>([]);
  const [locationByPersons, setLocationPyPersons] = useState<Array<ILocation>>([]);
  const [currentTab, setCurrentTab] = useState<'history' | 'messages'>('history');

  useEffect(() => {
    if (!id || !username) return;

    (async () => {
      await Promise.all([getPerson(id), getPersonDetail(id), getActionLogs(username), getByPersons(id)]);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, username]);

  const getActionLogs = async (username: string) => {
    try {
      const response = await actionLogApi.getActionLogs({
        username: username,
        pageIndex: PAGE_INDEX,
        pageSize: PAGE_SIZE,
        getall: false,
        personId: id,
      });
      setActionLogs(response.data.result.data);
    } catch (e) {
      handleApiError(e);
    }
  };

  const getPerson = async (id: string) => {
    try {
      const response = await personApi.getPerson(id);
      setPerson(response.data.result);
    } catch (e) {
      handleApiError(e);
    }
  };

  const getPersonDetail = async (id: string) => {
    try {
      const response = await personApi.getPersonDetail(id);
      setPersonDetail(response.data);
    } catch (e) {
      handleApiError(e);
    }
  };

  const getByPersons = async (id: string) => {
    try {
      const response = await locationApi.getByPersons(id);
      setLocationPyPersons(response.data.result);
    } catch (e) {
      handleApiError(e);
    }
  };

  const renderBatteryPercent = () => {
    const battery_percent: number = isNaN(Number(personDetail?.battery_percent))
      ? 0
      : Number(personDetail?.battery_percent);

    const label =
      battery_percent === 0
        ? t('detail.battery_percent.off')
        : battery_percent <= 20
          ? t('detail.battery_percent.low')
          : t('detail.battery_percent.normal');

    return `${battery_percent}% - ${label}`;
  };

  const formatInfo = (value: any) => {
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') return value ?? 0;
    if (typeof value === 'string' && value.trim() === '') return '-';

    return value ?? '-';
  };

  const formatHealthInfo = (value: any) => {
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') return value ?? 0;
    if (typeof value === 'string' && value.trim() === '') return '--';

    return value ?? '--';
  };

  return (
    <Box className={cx('person-detail-screen')}>
      <Box className='flex items-center bg-[#123B6D] h-20 px-6'>
        <Typography
          className='text-white sm:text-lg lg:text-xl font-semibold hover:cursor-pointer'
          onClick={() => navigate('/admin/persons')}
        >
          {t('list')}
        </Typography>
        <Box className='w-8 h-8 flex items-center justify-center'>
          <ArrowRight3Icon stroke='#fff' />
        </Box>
        <Typography className='text-white font-semibold sm:text-lg lg:text-xl'>{t('detail.title')}</Typography>
      </Box>

      <BaseRow className='gap-4 px-6 pt-6 mb-5'>
        <Box className='w-full h-[400px] bg-[#FAFDFF] rounded-xl p-4'>
          <Typography className='text-[#161A1F] font-semibold text-lg mb-3'>{t('detail.person')}</Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('cccd.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.cccd)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('full_name.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.full_name)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('birth_date.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {person?.birth_date ? dayjs(person?.birth_date).format('DD/MM/YYYY') : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('gender.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {person?.gender === 1 ? t('gender.male') : t('gender.female')}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('place_of_origin.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.place_of_origin)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('current_address.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.current_address)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('email.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.email)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('simno.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.simno)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none pt-0 px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('status.text')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{t('status.' + person?.active)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('imei_device')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(person?.imei)}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box className='w-full lg:h-[400px] h-auto overflow-auto bg-[#FAFDFF] rounded-xl p-4'>
          <Typography className='text-[#161A1F] font-semibold text-lg mb-3'>{t('detail.device')}</Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('imei_device')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(personDetail?.device_imei)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('assignStatus.text')}:</Typography>
                </TableCell>
                <TableCell className='border-none' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {personDetail?.device_imei === '' ? t('assignStatus.0') : t('assignStatus.1')}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0 pt-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.updatetime')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {personDetail?.updatetime ? dayjs(personDetail?.updatetime).format('DD/MM/YYYY HH:mm') : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.address')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatInfo(personDetail?.address)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.status.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {Number(personDetail?.status) >= 3 ? t('detail.status.disconnected') : t('detail.status.connected')}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.battery_percent.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{renderBatteryPercent()}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.anti_removal.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {t('detail.anti_removal.' + personDetail?.anti_removal)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.device_status.title')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {t('detail.device_status.' + personDetail?.device_status)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </BaseRow>

      <BaseRow className='gap-4 px-6 mb-5'>
        <Box className='flex-1 h-[350px] bg-white rounded-xl p-4'>
          <Box className='flex justify-between items-center mb-3'>
            <Typography className='text-[#161A1F] font-semibold text-lg'>{t('detail.health_summary')}</Typography>
            <ArrowRight2Icon
              className='hover:cursor-pointer w-3 h-3'
              stroke='#161A1F'
              onClick={() => navigate(`/admin/persons/${id}/health`)}
            />
          </Box>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.heart_rate')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {formatHealthInfo(personDetail?.heart_rate)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.sp02')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>{formatHealthInfo(personDetail?.spo2)}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.body_temperature')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {formatHealthInfo(personDetail?.body_temperature)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.blood_pressure')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {formatHealthInfo(personDetail?.blood_pressure)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.step_count')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {formatHealthInfo(personDetail?.step_count)}
                  </Typography>
                </TableCell>
              </TableRow>
              {/* Ẩn UI: Phát hiện không chuyển động
              <TableRow>
                <TableCell className='border-none px-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.no_movement_detected')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {formatHealthInfo(personDetail?.no_movement_detected)}
                  </Typography>
                </TableCell>
              </TableRow>
              */}
              {/* Ẩn UI: Cảnh báo ngã
              <TableRow>
                <TableCell className='border-none p-0'>
                  <Typography className='text-[#7C8796] text-base'>{t('detail.fall_detected')}:</Typography>
                </TableCell>
                <TableCell className='border-none p-0' align='right'>
                  <Typography className='text-[#161A1F] text-base'>
                    {formatHealthInfo(personDetail?.fall_detected)}
                  </Typography>
                </TableCell>
              </TableRow>
              */}
            </TableBody>
          </Table>
        </Box>

        <Box className="flex-1 h-[350px] bg-white rounded-xl p-4">
          <Box className="flex items-center justify-between mb-3">
            <Tabs
              value={currentTab}
              onChange={(_, value) => setCurrentTab(value)}
              aria-label="person-detail-tabs"
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  minHeight: 36,
                  padding: '6px 12px',
                  fontSize: '1.125rem', // text-lg = 18px
                  fontWeight: 600, // font-semibold
                  textTransform: 'none',
                },
              }}
            >
              <Tab
                value="history"
                label={t('detail.person_history')}
              />
              <Tab
                value="messages"
                label={t('detail.messages', { defaultValue: 'Messages' })}
              />
            </Tabs>

            {currentTab === 'history' && actionLogs?.length > 0 && (
              <ArrowRight2Icon
                className="hover:cursor-pointer w-4 h-4"
                stroke="#161A1F"
                onClick={() => navigate(`/admin/persons/${id}/history`)}
              />
            )}
          </Box>

          {currentTab === 'history' ? (
            <Box className="h-[270px] overflow-auto">
              <Table size="small">
                <TableBody>
                  {actionLogs?.map((row: IActionLog, index: number) => (
                    <TableRow
                      key={`${row.id}-${index}`}
                      sx={{
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell className="py-2 px-0">
                        <Box className="flex justify-between gap-4">
                          <Typography className="text-sm text-[#404854] break-words">
                            {row.description}
                          </Typography>

                          <Typography className="text-sm text-[#404854] whitespace-nowrap">
                            {row.action_time
                              ? dayjs(row.action_time).format('DD/MM/YYYY')
                              : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box className="h-[290px]">
              <PersonMessages personId={id || ''} />
            </Box>
          )}
        </Box>

      </BaseRow>

      <Box className='px-6 mb-5'>
        <Box className='bg-white rounded-xl p-4 max-h-[420px] overflow-auto'>
          <Box className='flex justify-between items-center mb-3'>
            <Typography className='text-[#161A1F] font-semibold text-lg'>{t('detail.location.title')}</Typography>
            {locationByPersons?.length > 0 && (
              <ArrowRight2Icon
                className='hover:cursor-pointer w-3 h-3'
                stroke='#161A1F'
                onClick={() => navigate(`/admin/persons/${id}/location-history`)}
              />
            )}
          </Box>
          <TableContainer className='rounded-t-xl border-b-0 bg-white overflow-x-auto'>
            <Table stickyHeader className='min-w-max'>
              <TableHead>
                <TableRow>
                  <TableCell className='bg-[#DCE8F5] text-[#161A1F] text-sm font-semibold w-fit' align='center'>
                    {t('detail.location.name')}
                  </TableCell>
                  <TableCell
                    className='bg-[#DCE8F5] text-[#161A1F] text-sm font-semibold border-l border-r border-[#EBF1F8] border-b-0 w-fit'
                    align='center'
                  >
                    {t('detail.location.type.title')}
                  </TableCell>
                  <TableCell
                    className='bg-[#DCE8F5] text-[#161A1F] text-sm font-semibold border-l border-r border-[#EBF1F8] border-b-0 w-fit'
                    align='center'
                  >
                    {t('province_ward.title')}
                  </TableCell>
                  <TableCell className='bg-[#DCE8F5] text-sm text-[#161A1F] font-semibold w-fit' align='center'>
                    {t('number_of_violations')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locationByPersons?.map((row: ILocation, index: number) => (
                  <TableRow key={index}>
                    <TableCell className='text-base text-center font-normal text-[#161A1F] border-l'>
                      {cast(row.name)}
                    </TableCell>
                    <TableCell className='text-base text-center font-normal text-[#161A1F] border-x'>
                      {t('detail.location.type.' + row.location_type)}
                    </TableCell>
                    <TableCell className='text-base text-center font-normal text-[#161A1F]'>{`${cast(row.ward_name)} ${row.province_name ? ' - ' + row.province_name : ''}`}</TableCell>
                    <TableCell className='text-base text-center font-normal text-[#161A1F] border-l'>
                      {cast(row.number_of_violations)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

    </Box>

  );
};

export default memo(PersonDetail);
